import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Search, X, ArrowRight, MessageCircle, Users, Radio, Sparkles,
  LayoutGrid, TrendingUp, Download, Mail, Phone, LogOut, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'clinity_admin_token';

/* ------------- shared helpers ------------- */
const scoreBand = (s) => {
  if (s == null) return { label: '—', color: '#94A3B8', bg: '#F1F5F9' };
  if (s >= 80) return { label: 'Thriving', color: '#059669', bg: '#D1FAE5' };
  if (s >= 65) return { label: 'Strong',   color: '#4F46E5', bg: '#E0E7FF' };
  if (s >= 50) return { label: 'Balanced', color: '#F59E0B', bg: '#FEF3C7' };
  return { label: 'Needs attention', color: '#DC2626', bg: '#FEE2E2' };
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const topWeakDims = (ds) => {
  if (!ds) return [];
  return Object.entries(ds).sort((a, b) => a[1] - b[1]).slice(0, 2).map(([k]) => k);
};

const rankDim = (k) => ({
  physical: 'Physical', mental: 'Mental', emotional: 'Emotional',
  financial: 'Financial', career: 'Career', lifestyle: 'Lifestyle',
  spiritual: 'Spiritual', self_care: 'Self-Care',
}[k] || k);

/* ------------- LOGIN ------------- */
function Login({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      const token = r.data?.token;
      if (!token) throw new Error('No token');
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch (e) {
      setErr('Invalid password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F8FAFC' }}>
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white p-8 rounded-3xl"
        style={{ border: '1px solid #E2E8F0', boxShadow: '0 24px 60px -30px rgba(15,23,42,0.2)' }}
        data-testid="admin-login-form"
      >
        <div className="text-center mb-7">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1 text-indigo-600">Clinity</div>
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>Admin Dashboard</div>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoFocus
          data-testid="admin-password-input"
          className="w-full px-4 py-3 rounded-xl outline-none text-sm"
          style={{ border: '1px solid #E2E8F0', fontFamily: 'Manrope, sans-serif' }}
        />
        {err && <div className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{err}</div>}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="w-full mt-4 py-3 rounded-full font-semibold text-white text-sm transition-all hover:-translate-y-0.5 disabled:opacity-60"
          style={{ background: '#0F172A', boxShadow: '0 12px 24px -12px rgba(15,23,42,0.5)' }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

/* ------------- SIDEBAR ------------- */
function Sidebar({ active, onChange, counts, onLogout }) {
  const items = [
    { key: 'overview',     label: 'Overview',                icon: LayoutGrid, count: null },
    { key: 'scores',       label: 'Wellness Scores',         icon: TrendingUp, count: counts.scores },
    { key: 'prescriptions',label: 'Prescriptions Unlocked',  icon: Sparkles,   count: counts.prescriptions },
    { key: 'experts',      label: 'Want Experts',            icon: Users,      count: counts.experts },
    { key: 'retreats',     label: 'Retreats & Spaces',       icon: Radio,      count: counts.retreats },
    { key: 'collaborate',  label: 'Work With Us',            icon: MessageCircle, count: counts.collaborate },
  ];
  return (
    <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r bg-white h-full sticky top-0" style={{ borderColor: '#E2E8F0' }} data-testid="admin-sidebar">
      <div className="px-6 py-6">
        <div className="text-lg font-bold" style={{ color: '#0F172A', fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>Clinity</div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 mt-0.5">Admin</div>
      </div>
      <nav className="flex-1 px-3 flex flex-col gap-1">
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = active === it.key;
          return (
            <button
              key={it.key}
              onClick={() => onChange(it.key)}
              data-testid={`sidebar-${it.key}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${isActive ? 'text-indigo-600' : 'text-slate-600 hover:text-slate-900'}`}
              style={{ background: isActive ? '#EEF2FF' : 'transparent' }}
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1 text-left">{it.label}</span>
              {typeof it.count === 'number' && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: isActive ? '#4F46E5' : '#F1F5F9', color: isActive ? '#fff' : '#475569' }}>{it.count}</span>}
            </button>
          );
        })}
      </nav>
      <div className="px-3 pb-6">
        <button onClick={onLogout} data-testid="admin-logout-btn" className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </aside>
  );
}

/* ------------- SECTION FILTERS ------------- */
// Maps each section to a predicate against a person's source_list
const SECTIONS = {
  scores:        { title: 'People who generated Wellness Score',   predicate: (p) => p.score != null, subtitle: 'Everyone who completed their assessment.' },
  prescriptions: { title: 'Unlocked Wellness Prescriptions',       predicate: (p) => (p.prescriptions_count || 0) > 0 || p.source_list?.includes('prescription_unlock'), subtitle: 'Users who unlocked their prescription.' },
  experts:       { title: 'Want Wellness Experts',                 predicate: (p) => (p.source_list || []).some((s) => ['experts_personalized','experts_connect_interest','result_want_access','expert_contact_lock','experts_premium','result_premium'].includes(s)), subtitle: 'Users who clicked Connect With Experts / I Want Access.' },
  retreats:      { title: 'Wellness Retreats & Spaces',            predicate: (p) => (p.source_list || []).some((s) => ['spaces_personalized','spaces_recommendations','clinics_hero'].includes(s)), subtitle: 'Users interested in retreats & spaces.' },
  collaborate:   { title: 'Work With Us / Collaborators',          predicate: (p) => (p.source_list || []).includes('collaborate_expert'), subtitle: 'Prospective experts who tapped Collaborate With Us.' },
};

/* ------------- CONVERSION CARDS ------------- */
function Overview({ people }) {
  const rows = people.people || [];
  const scored = rows.filter((p) => p.score != null);
  const prescriptions = rows.filter((p) => SECTIONS.prescriptions.predicate(p));
  const experts = rows.filter((p) => SECTIONS.experts.predicate(p));
  const retreats = rows.filter((p) => SECTIONS.retreats.predicate(p));
  const collab = rows.filter((p) => SECTIONS.collaborate.predicate(p));
  const waTaps = rows.reduce((a, p) => a + (p.signal_count || 0), 0);
  const conv = (n, d) => (d ? Math.round((n / d) * 100) : 0);

  const cards = [
    { label: 'Wellness Scores', value: scored.length, sub: `${people.anonymous_signal_count} anonymous signals`, tid: 'ov-scores' },
    { label: 'Prescriptions Unlocked', value: prescriptions.length, sub: `${conv(prescriptions.length, scored.length)}% of scored`, tid: 'ov-prescriptions' },
    { label: 'Expert Interest', value: experts.length, sub: `${conv(experts.length, scored.length)}% of scored`, tid: 'ov-experts' },
    { label: 'Retreat Interest', value: retreats.length, sub: `${conv(retreats.length, scored.length)}% of scored`, tid: 'ov-retreats' },
    { label: 'Work With Us', value: collab.length, sub: 'Prospective experts', tid: 'ov-collab' },
    { label: 'WhatsApp Taps', value: waTaps, sub: `Across ${rows.length} people`, tid: 'ov-wa' },
  ];

  return (
    <div data-testid="overview-panel" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <OverviewCard key={c.tid} label={c.label} value={c.value} sub={c.sub} testid={c.tid} />
        ))}
      </div>

      <div className="rounded-2xl bg-white p-5" style={{ border: '1px solid #E2E8F0' }} data-testid="ov-by-source">
        <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Signals by source</div>
        <div className="space-y-2">
          {Object.entries(people.by_source || {}).length === 0 && <div className="text-sm text-slate-400">No signals yet.</div>}
          {Object.entries(people.by_source || {}).sort((a,b)=>b[1]-a[1]).map(([src, n]) => {
            const max = Math.max(...Object.values(people.by_source));
            const pct = Math.max(4, (n / max) * 100);
            return (
              <div key={src} className="flex items-center gap-3">
                <div className="w-56 text-xs font-semibold text-slate-700 truncate">{src}</div>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#F1F5F9' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #6366F1, #8B5CF6)' }} />
                </div>
                <div className="w-8 text-right text-xs font-bold text-slate-900 tabular-nums">{n}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------- SECTION VIEW (People table filtered) ------------- */
function SectionView({ sectionKey, rows, onSelect, onExport, selected, onToggleAll, onToggleOne }) {
  const meta = SECTIONS[sectionKey];
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>{meta.title}</div>
          <div className="text-sm text-slate-500 mt-0.5">{meta.subtitle} · {rows.length} {rows.length === 1 ? 'person' : 'people'}</div>
        </div>
        <button onClick={onExport} data-testid={`export-${sectionKey}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-white hover:-translate-y-0.5 transition-all" style={{ background: '#0F172A' }}>
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <PeopleTable rows={rows} onSelect={onSelect} selected={selected} onToggleAll={onToggleAll} onToggleOne={onToggleOne} />
    </div>
  );
}

/* ------------- OVERVIEW CARD ------------- */
function OverviewCard({ label, value, sub, testid, accent = '#4F46E5' }) {
  return (
    <div data-testid={testid} className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E2E8F0', boxShadow: '0 2px 6px rgba(15,23,42,0.04)' }}>
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div className="mt-2 text-3xl font-bold" style={{ color: '#0F172A', fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>{value}</div>
      {sub && <div className="mt-1.5 text-xs" style={{ color: accent, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

function Overview_OLD_REMOVED() { return null; }

/* ------------- PEOPLE ------------- */
function PeopleTable({ rows, onSelect, selected, onToggleAll, onToggleOne }) {
  const hasSelection = selected && onToggleOne;
  const allChecked = hasSelection && rows.length > 0 && rows.every((r) => selected.has(r.contact_key));
  return (
    <div className="rounded-2xl overflow-hidden bg-white" style={{ border: '1px solid #E2E8F0' }} data-testid="people-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b" style={{ borderColor: '#E2E8F0' }}>
              {hasSelection && (
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => onToggleAll(e.target.checked ? rows.map((r) => r.contact_key) : [])}
                    data-testid="select-all-checkbox"
                    className="w-3.5 h-3.5 rounded cursor-pointer accent-indigo-600"
                  />
                </th>
              )}
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Score</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Signals</th>
              <th className="px-4 py-3 text-right">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={hasSelection ? 7 : 6} className="px-4 py-8 text-center text-slate-400">No people match your filters.</td></tr>
            )}
            {rows.map((r) => {
              const band = scoreBand(r.score);
              const priority = topWeakDims(r.dimension_scores);
              const isSelected = hasSelection && selected.has(r.contact_key);
              return (
                <tr
                  key={r.contact_key}
                  onClick={(e) => {
                    // Ignore clicks on checkbox column
                    if (e.target.tagName === 'INPUT') return;
                    onSelect(r);
                  }}
                  className="border-b hover:bg-slate-50 cursor-pointer transition-colors"
                  style={{ borderColor: '#F1F5F9' }}
                  data-testid={`people-row-${r.contact_key}`}
                >
                  {hasSelection && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleOne(r.contact_key)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`select-${r.contact_key}`}
                        disabled={!r.whatsapp}
                        className="w-3.5 h-3.5 rounded cursor-pointer accent-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={r.whatsapp ? 'Select for bulk WhatsApp' : 'No WhatsApp number available'}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-semibold text-slate-900">{r.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">
                    <div className="flex flex-col gap-0.5">
                      {r.whatsapp && <span className="inline-flex items-center gap-1 text-xs"><Phone className="w-3 h-3" />{r.whatsapp}</span>}
                      {r.email && <span className="inline-flex items-center gap-1 text-xs"><Mail className="w-3 h-3" />{r.email}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.score != null ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-base font-bold text-slate-900 tabular-nums">{r.score}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ color: band.color, background: band.bg }}>{band.label}</span>
                      </span>
                    ) : (<span className="text-slate-400 text-xs">Not taken</span>)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {priority.map((k) => (
                        <span key={k} className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ color: '#991B1B', background: '#FEE2E2' }}>{rankDim(k)}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.source_list.slice(0, 3).map((s) => (
                        <span key={s} className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-indigo-700" style={{ background: '#EEF2FF' }}>{s}</span>
                      ))}
                      {r.source_list.length > 3 && <span className="text-xs text-slate-400">+{r.source_list.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500 text-xs">{timeAgo(r.last_seen)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------- PERSON DRAWER ------------- */
function PersonDrawer({ contactKey, token, onClose, sectionKey }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);

  const loadDetail = useCallback(() => {
    setLoading(true);
    return axios.get(`${API}/admin/people/${encodeURIComponent(contactKey)}`, { headers: { 'X-Admin-Token': token } })
      .then(r => setDetail(r.data))
      .finally(() => setLoading(false));
  }, [contactKey, token]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const updateStatus = async (status) => {
    setSavingStatus(true);
    try {
      await axios.patch(`${API}/admin/people/${encodeURIComponent(contactKey)}/collaborator-status`, { status }, { headers: { 'X-Admin-Token': token } });
      await loadDetail();
    } finally { setSavingStatus(false); }
  };

  const person = detail;
  const latest = person?.assessments?.[0];
  const weakLabels = latest ? topWeakDims(latest.dimension_scores).map(rankDim).join(' and ') : null;
  const waMsg = latest
    ? `Hi ${person?.name || 'there'}, we saw your Wellness Score of ${latest.total_score}${weakLabels ? ` and noticed opportunity in ${weakLabels}.` : '.'} Would love to help you move forward.`
    : `Hi ${person?.name || 'there'}, this is Clinity. We saw your interest and would love to help you get started.`;
  const waDigits = person?.whatsapp ? String(person.whatsapp).replace(/\D/g, '') : null;
  const waHref = waDigits ? `https://wa.me/${waDigits}?text=${encodeURIComponent(waMsg)}` : null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="person-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="ml-auto w-full max-w-[520px] bg-white h-full overflow-y-auto relative flex flex-col" style={{ boxShadow: '-24px 0 60px -20px rgba(15,23,42,0.25)' }}>
        <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E2E8F0' }}>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Person</div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100" data-testid="drawer-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 px-6 py-6 space-y-6">
          {loading && <div className="text-sm text-slate-400">Loading…</div>}
          {!loading && person && (
            <>
              {/* Identity */}
              <div>
                <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.015em' }}>{person.name || '—'}</div>
                <div className="mt-1 space-y-0.5 text-sm text-slate-500">
                  {person.whatsapp && <div className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{person.whatsapp}</div>}
                  {person.email && <div className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{person.email}</div>}
                  {person.city && <div>{person.city}</div>}
                </div>
                {waHref && (
                  <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="drawer-wa-btn" className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white no-underline transition-all hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                    <MessageCircle className="w-4 h-4" /> WhatsApp {person.name?.split(' ')[0] || 'them'}
                  </a>
                )}
              </div>

              {/* Latest score */}
              {latest && (
                <div className="rounded-2xl p-4" style={{ background: '#EEF2FF', border: '1px solid #E0E7FF' }} data-testid="drawer-score">
                  <div className="flex items-baseline gap-3">
                    <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{latest.total_score}</div>
                    <div className="text-xs font-bold uppercase tracking-widest" style={{ color: scoreBand(latest.total_score).color }}>{scoreBand(latest.total_score).label}</div>
                    <div className="text-xs text-slate-400 ml-auto">{timeAgo(latest.created_at)}</div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {Object.entries(latest.dimension_scores || {}).sort((a,b)=>b[1]-a[1]).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{rankDim(k)}</span>
                        <span className="font-bold text-slate-900 tabular-nums">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Collaborator status controls (Work With Us) */}
              {sectionKey === 'collaborate' && (
                <div className="rounded-2xl p-4 bg-white" style={{ border: '1px solid #E2E8F0' }} data-testid="drawer-collab-status">
                  <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Application status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['new','contacted','reviewing','approved','rejected'].map((s) => {
                      const active = (person.collaborator_status || 'new') === s;
                      const color = s === 'approved' ? '#059669' : s === 'rejected' ? '#DC2626' : s === 'reviewing' ? '#D97706' : s === 'contacted' ? '#4F46E5' : '#64748B';
                      return (
                        <button
                          key={s}
                          onClick={() => updateStatus(s)}
                          disabled={savingStatus}
                          data-testid={`collab-status-${s}`}
                          className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all disabled:opacity-60 hover:-translate-y-0.5"
                          style={{
                            color: active ? '#fff' : color,
                            background: active ? color : `${color}15`,
                            border: `1px solid ${active ? color : `${color}30`}`,
                          }}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Signals timeline */}
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Signals ({person.signals?.length || 0})</div>
                <div className="space-y-2" data-testid="drawer-signals">
                  {(person.signals || []).map((s, i) => (
                    <div key={i} className="rounded-xl p-3 bg-white text-xs" style={{ border: '1px solid #E2E8F0' }}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-indigo-700">{s.source || s.kind}</span>
                        <span className="text-slate-400">{timeAgo(s.created_at)}</span>
                      </div>
                      <div className="mt-1 text-slate-500 space-x-2">
                        {s.preference && <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#FEF3C7', color: '#92400E' }}>{s.preference}</span>}
                        {s.contact_type && <span className="text-[10px]">contact: {s.contact_type}</span>}
                      </div>
                    </div>
                  ))}
                  {(!person.signals || person.signals.length === 0) && <div className="text-sm text-slate-400">No signals yet.</div>}
                </div>
              </div>

              {/* Prescriptions */}
              {person.prescriptions?.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Prescriptions ({person.prescriptions.length})</div>
                  <div className="space-y-2">
                    {person.prescriptions.map((rx) => (
                      <div key={rx.prescription_id} className="rounded-xl p-3 bg-white text-xs" style={{ border: '1px solid #E2E8F0' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-900">{rx.prescription_id}</span>
                          <a href={`${API}/prescription/${rx.assessment_id}/pdf`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-semibold no-underline">Open PDF</a>
                        </div>
                        <div className="text-slate-400 mt-1">{timeAgo(rx.created_at)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------- SIGNALS TABLE ------------- */
function SignalsPanel({ people }) {
  const rows = useMemo(() => {
    const all = [];
    for (const p of people.people) {
      (p.source_list || []).forEach((s) => {
        all.push({ contact_key: p.contact_key, name: p.name, whatsapp: p.whatsapp, source: s, last_seen: p.last_seen, score: p.score });
      });
    }
    return all.sort((a,b) => (b.last_seen || '').localeCompare(a.last_seen || ''));
  }, [people]);

  return (
    <div className="rounded-2xl overflow-hidden bg-white" style={{ border: '1px solid #E2E8F0' }} data-testid="signals-panel">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b" style={{ borderColor: '#E2E8F0' }}>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Person</th>
              <th className="px-4 py-3">Score</th>
              <th className="px-4 py-3 text-right">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No signals yet.</td></tr>}
            {rows.slice(0, 200).map((r, i) => (
              <tr key={i} className="border-b" style={{ borderColor: '#F1F5F9' }}>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-indigo-700" style={{ background: '#EEF2FF' }}>{r.source}</span></td>
                <td className="px-4 py-3 text-slate-600">{r.name || '—'} <span className="text-xs text-slate-400 ml-1">{r.whatsapp || ''}</span></td>
                <td className="px-4 py-3 tabular-nums">{r.score ?? '—'}</td>
                <td className="px-4 py-3 text-right text-xs text-slate-500">{timeAgo(r.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------- ANONYMOUS ------------- */
function AnonymousPanel({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/anonymous-signals?limit=200`, { headers: { 'X-Admin-Token': token } })
      .then(r => setData(r.data));
  }, [token]);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="rounded-2xl overflow-hidden bg-white" style={{ border: '1px solid #E2E8F0' }} data-testid="anonymous-panel">
      <div className="px-4 py-3 text-xs text-slate-500 border-b" style={{ borderColor: '#F1F5F9' }}>
        These are waitlist taps with no contact info attached (only IP hash).
        Use them for demand-signal analytics only.
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b" style={{ borderColor: '#E2E8F0' }}>
            <th className="px-4 py-3">Source</th>
            <th className="px-4 py-3">Extra</th>
            <th className="px-4 py-3 text-right">When</th>
          </tr>
        </thead>
        <tbody>
          {data.signals.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">No anonymous signals.</td></tr>}
          {data.signals.map((s, i) => (
            <tr key={i} className="border-b" style={{ borderColor: '#F1F5F9' }}>
              <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-indigo-700" style={{ background: '#EEF2FF' }}>{s.source || '—'}</span></td>
              <td className="px-4 py-3 text-xs text-slate-500">
                {s.preference && <span>pref: {s.preference}</span>}
                {s.expert_id && <span> · expert: {s.expert_id}</span>}
                {s.contact_type && <span> · {s.contact_type}</span>}
              </td>
              <td className="px-4 py-3 text-right text-xs text-slate-500">{timeAgo(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------- CSV EXPORT ------------- */
function toCSV(rows) {
  if (!rows.length) return '';
  const cols = ['name', 'whatsapp', 'email', 'city', 'score', 'assessments_count', 'signal_count', 'source_list', 'last_seen'];
  const esc = (v) => {
    if (v == null) return '';
    const s = Array.isArray(v) ? v.join('|') : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
}

function downloadCSV(rows, filename) {
  const blob = new Blob([toCSV(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

/* ------------- MAIN ------------- */
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState('overview');
  const [people, setPeople] = useState({ people: [], total: 0, by_source: {}, anonymous_signal_count: 0 });
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/people?limit=500`, { headers: { 'X-Admin-Token': token } });
      setPeople(r.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const rows = people.people || [];
    const bySearch = !q ? rows : rows.filter((r) => [r.name, r.email, r.whatsapp].filter(Boolean).some((v) => String(v).toLowerCase().includes(q.toLowerCase())));
    if (tab === 'overview') return bySearch;
    const meta = SECTIONS[tab];
    return meta ? bySearch.filter(meta.predicate) : bySearch;
  }, [people, q, tab]);

  if (!token) return <Login onLogin={setToken} />;

  const rows = people.people || [];
  const counts = {
    scores:        rows.filter((p) => p.score != null).length,
    prescriptions: rows.filter((p) => SECTIONS.prescriptions.predicate(p)).length,
    experts:       rows.filter((p) => SECTIONS.experts.predicate(p)).length,
    retreats:      rows.filter((p) => SECTIONS.retreats.predicate(p)).length,
    collaborate:   rows.filter((p) => SECTIONS.collaborate.predicate(p)).length,
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC', fontFamily: 'Manrope, sans-serif' }} data-testid="admin-page">
      <div className="flex">
        <Sidebar active={tab} onChange={setTab} counts={counts} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />

        <main className="flex-1 min-w-0">
          {/* Top bar */}
          <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b" style={{ borderColor: '#E2E8F0' }} data-testid="admin-topbar">
            <div className="px-6 py-4 flex items-center gap-3">
              <div className="text-sm font-bold text-slate-900 capitalize" style={{ fontFamily: 'Outfit, sans-serif' }}>{tab === 'overview' ? 'Overview' : SECTIONS[tab]?.title || tab}</div>
              <div className="flex-1 flex items-center gap-2 max-w-md ml-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, email, WhatsApp"
                    className="w-full pl-9 pr-3 py-2 rounded-full text-sm outline-none bg-slate-50"
                    style={{ border: '1px solid #E2E8F0' }}
                    data-testid="admin-search-input"
                  />
                </div>
              </div>
              <button onClick={refresh} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-slate-700 bg-white transition-all hover:-translate-y-0.5" style={{ border: '1px solid #E2E8F0' }}>
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6" data-testid="admin-content">
            {tab === 'overview' && <Overview people={people} />}
            {tab !== 'overview' && (
              <SectionView
                sectionKey={tab}
                rows={filtered}
                onSelect={(r) => setSelected(r.contact_key)}
                onExport={() => downloadCSV(filtered, `clinity-${tab}.csv`)}
              />
            )}
          </div>
        </main>
      </div>

      {selected && <PersonDrawer contactKey={selected} token={token} onClose={() => setSelected(null)} sectionKey={tab} />}
    </div>
  );
}
